import React, { useEffect, useRef, useState } from "react";
import { Carousel } from "react-bootstrap";
import { RiArrowDropDownFill, RiArrowDropUpFill, RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import image1 from "../../assets/svgs/image1.svg";
import axios from "axios";
import "./Carousel.css";

const Carousels = (props) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // for smooth exit
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [textColor, setTextColor] = useState("white");

  const all_locations = useSelector((state) => state?.auth?.all_locations);
  const location_label = useSelector((state) => state?.auth?.location_label);

  const history = useHistory();

  useEffect(() => {
    // Returns the landscape images for an image_type, or null if the request failed.
    const requestImages = async (imageType, silentOnError) => {
      try {
        const response = await axios.post("display-images", {
          business_id: props.business_id,
          location_id: props.location_id,
          image_type: imageType,
        });
        return response?.data?.data?.filter((item) => item.orientation === "Landscape") || [];
      } catch (error) {
        if (!silentOnError) console.error("Error fetching images:", error);
        return null;
      }
    };

    const getImages = async () => {
      try {
        // Prefer the dedicated "banner(QR)" images; fall back to "QR" when none are available.
        // Both are requested together so stores without banners load as fast as before.
        const [banner, qr] = await Promise.all([
          requestImages("banner(QR)", true),
          requestImages("QR", false),
        ]);
        setImages(banner?.length ? banner : qr || []);
      } finally {
        setLoading(false);
      }
    };

    getImages();
  }, [props.business_id, props.location_id]);

  const handleLocationSelect = (id, index) => {
    if (id !== localStorage.getItem("location_id")) {
      setSelectedIndex(index);
      const params = new URLSearchParams(window.location.search);
      const businessId =
        params.get("business_id") || localStorage.getItem("business_id");
      history.push(`/?business_id=${businessId}&location_id=${id}`);
      window.location.reload();
    }
  };

  useEffect(() => {
    if (location_label) {
      const params = new URLSearchParams(window.location.search);
      const businessId = params.get("business_id");
      if (businessId) {
        history.push(
          `/?business_id=${businessId}&location_id=${location_label?.id}`
        );
      }
    }
  }, [location_label]);

  const detectBrightness = async (imgSrc) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = imgSrc;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const width = (canvas.width = 40);
        const height = (canvas.height = 40);
        ctx.drawImage(img, 0, 0, width, height);

        try {
          const topHeight = Math.floor(height * 0.3);
          const imageData = ctx.getImageData(0, 0, width, topHeight);
          const data = imageData.data;
          let totalBrightness = 0;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
            totalBrightness += brightness;
          }

          const avgBrightness = totalBrightness / (data.length / 4);
          resolve(avgBrightness);
        } catch (err) {
          console.warn("Brightness detection failed:", err);
          resolve(255);
        }
      };
      img.onerror = () => resolve(255);
    });
  };

  const handleSlideChange = async (index) => {
    const imgSrc = images?.[index]?.image_url || image1;
    const brightness = await detectBrightness(imgSrc);
    setTextColor(brightness < 65 ? "white" : "black");
  };

  useEffect(() => {
    if (images?.[0]) handleSlideChange(0);
  }, [images]);

  // Close the dropdown on outside click
  const selectorRef = useRef(null);
  useEffect(() => {
    if (!showDropdown) return undefined;
    const onOutside = (e) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [showDropdown]);

  // Smooth dropdown unmount
  useEffect(() => {
    if (showDropdown) {
      setIsVisible(true);
    } else {
      const timeout = setTimeout(() => setIsVisible(false), 250);
      return () => clearTimeout(timeout);
    }
  }, [showDropdown]);

  return (
    <div style={{ position: "relative" }}>
      {/* Inject keyframes */}
      <style>
        {`
          @keyframes fadeSlideScaleIn {
            0% {
              opacity: 0;
              transform: translateY(-10px) scale(0.95);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes fadeSlideScaleOut {
            0% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
            100% {
              opacity: 0;
              transform: translateY(-10px) scale(0.95);
            }
          }
        `}
      </style>

      {/* Dropdown trigger */}
      <div className="location-selector-wrapper" ref={selectorRef}>
        <div
          className="location-trigger"
          onClick={() => setShowDropdown(!showDropdown)}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "150px",
            fontSize: "15px",
            color: textColor,
            backgroundColor: textColor === "white" ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.7)",
            transition: "all 0.4s ease",
            textShadow:
              textColor === "white"
                ? "0 2px 4px rgba(0,0,0,0.6)"
                : "0 2px 4px rgba(255,255,255,0.4)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
        >
          {location_label?.label}
          {showDropdown ? (
            <RiArrowDropUpFill
              style={{ fontSize: "24px", marginLeft: "4px", color: textColor }}
            />
          ) : (
            <RiArrowDropDownFill
              style={{ fontSize: "24px", marginLeft: "4px", color: textColor }}
            />
          )}
        </div>

        {/* Animated dropdown */}
        {isVisible && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: "0",
              left: "0",
              background: "rgba(0,0,0,0.85)",
              backdropFilter: "blur(8px)",
              border: "none",
              borderRadius: "10px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
              marginTop: "6px",
              minWidth: "100%",
              width: "max-content",
              maxWidth: "min(320px, calc(100vw - 32px))",
              maxHeight: "240px",
              overflowY: "auto",
              zIndex: 1001,
              animation: showDropdown
                ? "fadeSlideScaleIn 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards"
                : "fadeSlideScaleOut 0.25s ease forwards",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              transformOrigin: "top right",
            }}
          >
            {all_locations?.length > 0 && all_locations?.map((item, index) => {
              const isSelected = selectedIndex === index || String(item.id) === String(location_label?.id);
              return (
              <div
                key={index}
                onClick={() => handleLocationSelect(item.id, index, item.label)}
                style={{
                  padding: "12px 16px",
                  cursor: "pointer",
                  color: "white",
                  fontSize: "15px",
                  fontWeight: isSelected ? "600" : "400",
                  background:
                    isSelected
                      ? "linear-gradient(90deg, #c21010 0%, #e63946 100%)"
                      : "transparent",
                  transition: "all 0.3s ease",
                  borderBottom:
                    index !== all_locations.length - 1
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "none",
                  textAlign: "left",
                  width: "100%",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected)
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected)
                    e.currentTarget.style.background = "transparent";
                }}
              >
                {item.label}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Carousel */}
      <Carousel
        className="premium-carousel"
        onSlid={handleSlideChange}
        prevIcon={
          <div className="carousel-control-btn">
            <RiArrowLeftSLine />
          </div>
        }
        nextIcon={
          <div className="carousel-control-btn">
            <RiArrowRightSLine />
          </div>
        }
      >
        {loading ? (
          <Carousel.Item className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="loading-shimmer" style={{ width: "100%", height: "100%", background: "#f3f4f6" }}></div>
          </Carousel.Item>
        ) : images?.length > 0 ? (
          images.map((item, index) => {
            // Only banner(QR) slides carry a header/subtitle overlay
            const isBanner = item.image_type?.toLowerCase() === "banner(qr)";
            const header = isBanner ? item.banner_header?.trim() : "";
            const subtitle = isBanner ? item.banner_subtitle?.trim() : "";
            return (
              <Carousel.Item key={index}>
                <img className="d-block w-100" src={item.image_url || '/images/default_placeholder.png'} onError={(e) => { e.target.onerror = null; e.target.src = '/images/default_placeholder.png'; }} alt={`Slide ${index}`} />
                {(header || subtitle) && (
                  <div className="carousel-banner-text">
                    {header && <h2 className="carousel-banner-title">{header}</h2>}
                    {subtitle && <p className="carousel-banner-subtitle">{subtitle}</p>}
                  </div>
                )}
              </Carousel.Item>
            );
          })
        ) : (
          <Carousel.Item>
            <img className="d-block w-100" src={image1} alt={`Slide`} />
          </Carousel.Item>
        )}
      </Carousel>
    </div>
  );
};

export default Carousels;
